import {
  Activity,
  ArrowUpRight,
  BookOpen,
  BriefcaseBusiness,
  ChartNoAxesCombined,
  DollarSign,
  Megaphone,
  Store,
  UserRoundCog
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { ComponentType, CSSProperties } from "react";
import type {
  BusinessAnalysisSnapshot,
  DashboardSummary,
  HealthMapEdge,
  HealthMapNode,
  IntegrationJob,
  IntegrationRun,
  InvoiceAnalysisSnapshot,
  MarketingAnalyticsSnapshot,
  ProfitLossSnapshot,
  StationAnalyticsSnapshot
} from "../../shared/types";
import { useAuthUser } from "../auth/AuthProvider";
import { StatsCounter } from "../components/common/StatsCounter";
import { KineticTextLoader } from "../components/common/KineticTextLoader";
import type { ViewId } from "../components/layout/AppShell";
import { getBusinessAnalysis } from "../services/businessAnalysisService";
import { getInvoiceAnalysis } from "../services/orderPlanningService";
import { getMarketingAnalytics } from "../services/marketingService";
import { getProfitLoss } from "../services/profitLossService";
import { getStationAnalytics } from "../services/stationAnalyticsService";
import { getRequired } from "../services/apiClient";
import { getHealthMap } from "../services/healthMapService";

interface LandingPageProps {
  onNavigate: (view: ViewId) => void;
}

interface LandingModule {
  id: string;
  label: string;
  description: string;
  detail: string;
  view: ViewId;
  icon: ComponentType<{ size?: number; "aria-hidden"?: boolean }>;
  tone: string;
}

interface HealthLandingSnapshot {
  jobs: IntegrationJob[];
  summary: DashboardSummary;
  runs: IntegrationRun[];
}

interface LandingSnapshots {
  stations?: StationAnalyticsSnapshot | null;
  reports?: InvoiceAnalysisSnapshot | null;
  profitLoss?: ProfitLossSnapshot | null;
  marketing?: MarketingAnalyticsSnapshot | null;
  health?: HealthLandingSnapshot | null;
  healthMap?: { nodes: HealthMapNode[]; edges: HealthMapEdge[] } | null;
  businessAnalysis?: BusinessAnalysisSnapshot | null;
}

const modules: LandingModule[] = [
  { id: "stations", label: "Stations", description: "Station performance and operating trends", detail: "FIELD OPERATIONS", view: "station-analytics", icon: Store, tone: "mint" },
  { id: "reports", label: "Reports", description: "Sales, vendor and market reporting", detail: "REPORTING", view: "reports", icon: ChartNoAxesCombined, tone: "cyan" },
  { id: "profit-loss", label: "Profit & Loss", description: "Financial performance by period", detail: "FINANCIALS", view: "profit-loss", icon: DollarSign, tone: "gold" },
  { id: "business-analysis", label: "Business Analysis", description: "Explore scenarios and business drivers", detail: "ANALYTICS", view: "business-analysis", icon: BriefcaseBusiness, tone: "blue" },
  { id: "marketing", label: "Marketing", description: "Campaign and audience performance", detail: "GROWTH", view: "marketing", icon: Megaphone, tone: "coral" },
  { id: "health", label: "Health Monitor", description: "Live job status, runs and dependencies", detail: "LIVE SYSTEMS", view: "dashboard", icon: Activity, tone: "mint" },
  { id: "access-admin", label: "Access Admin", description: "Manage station and portal access", detail: "ADMINISTRATION", view: "access-admin", icon: UserRoundCog, tone: "violet" },
  { id: "documentation", label: "Documentation", description: "Guides, definitions and operating notes", detail: "KNOWLEDGE", view: "about", icon: BookOpen, tone: "slate" }
];

function torontoDate(offsetDays = 0): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    day: "2-digit",
    month: "2-digit",
    timeZone: "America/Toronto",
    year: "numeric"
  }).formatToParts(new Date());
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  const date = new Date(Date.UTC(Number(values.year), Number(values.month) - 1, Number(values.day) + offsetDays));
  return date.toISOString().slice(0, 10);
}

function torontoDay(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const parts = new Intl.DateTimeFormat("en-CA", {
    day: "2-digit",
    month: "2-digit",
    timeZone: "America/Toronto",
    year: "numeric"
  }).formatToParts(date);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
}

function MiniBars({ values, label }: { values: number[]; label: string }) {
  const max = Math.max(1, ...values);
  return (
    <span className="landing-mini-bars" role="img" aria-label={label}>
      {values.map((value, index) => (
        <i key={index} style={{ "--bar-height": `${Math.max(7, (value / max) * 100)}%` } as CSSProperties} />
      ))}
    </span>
  );
}

function HealthRunChart({ runs }: { runs: IntegrationRun[] }) {
  const dates = Array.from({ length: 7 }, (_, index) => torontoDate(index - 6));
  const totals = new Map(dates.map((date) => [date, { healthy: 0, issues: 0 }]));
  runs.forEach((run) => {
    const day = torontoDay(run.actualStartTime || run.expectedStartTime);
    const point = totals.get(day);
    if (!point) return;
    if (run.status === "SUCCEEDED") point.healthy += 1;
    else if (["FAILED", "LATE", "MISSING", "LONG_RUNNING"].includes(run.status)) point.issues += 1;
  });
  const max = Math.max(1, ...[...totals.values()].map((point) => point.healthy + point.issues));

  return (
    <span className="health-run-chart" role="img" aria-label="Pipeline runs for the last seven days">
      {dates.map((date) => {
        const point = totals.get(date)!;
        const total = point.healthy + point.issues;
        const healthyHeight = total ? (point.healthy / max) * 100 : 0;
        const issueHeight = total ? (point.issues / max) * 100 : 0;
        const dayLabel = new Intl.DateTimeFormat("en-CA", { timeZone: "America/Toronto", weekday: "short" }).format(new Date(`${date}T12:00:00`));
        return (
          <span className="health-run-chart__day" key={date}>
            <span className="health-run-chart__track">
              <i className="health-run-chart__healthy" style={{ height: `${healthyHeight}%` }} />
              <i className="health-run-chart__issues" style={{ height: `${issueHeight}%` }} />
            </span>
            <small>{dayLabel}</small>
          </span>
        );
      })}
      <span className="health-run-chart__legend"><i /> Successful <i /> Needs attention</span>
    </span>
  );
}

const sourceTypeLabels: Record<string, string> = {
  ACR: "ACR",
  AZURE_MONITOR: "Azure Monitor",
  CUSTOM: "Custom",
  MARKETING: "Marketing",
  POWER_BI: "Power BI",
  SQL_ETL: "DB / SQL ETL",
  SYNAPSE: "Synapse"
};

function HealthSourceTypeSummary({ jobs }: { jobs: IntegrationJob[] }) {
  const groups = new Map<string, IntegrationJob[]>();
  jobs.filter((job) => job.isActive).forEach((job) => {
    const group = groups.get(job.sourceType) || [];
    group.push(job);
    groups.set(job.sourceType, group);
  });
  const sources = [...groups.entries()].sort(([left], [right]) =>
    (sourceTypeLabels[left] || left).localeCompare(sourceTypeLabels[right] || right)
  );

  return (
    <span className="health-source-summary" aria-label="Current active job status by source type">
      {sources.map(([sourceType, sourceJobs]) => {
        const counts = {
          succeeded: sourceJobs.filter((job) => job.status === "SUCCEEDED").length,
          running: sourceJobs.filter((job) => job.status === "RUNNING").length,
          failed: sourceJobs.filter((job) => job.status === "FAILED").length,
          scheduled: sourceJobs.filter((job) => job.status === "SCHEDULED").length,
          attention: sourceJobs.filter((job) => ["MISSING", "LATE", "LONG_RUNNING", "CANCELLED", "UNKNOWN"].includes(job.status)).length
        };
        const total = sourceJobs.length;
        return (
          <span className="health-source-summary__row" key={sourceType}>
            <span className="health-source-summary__heading">
              <strong>{sourceTypeLabels[sourceType] || sourceType}</strong>
              <small>{counts.succeeded}/{total} succeeded</small>
            </span>
            <span className="health-source-summary__track" aria-hidden="true">
              {counts.succeeded > 0 && <i className="is-succeeded" style={{ width: `${(counts.succeeded / total) * 100}%` }} />}
              {counts.running > 0 && <i className="is-running" style={{ width: `${(counts.running / total) * 100}%` }} />}
              {counts.failed > 0 && <i className="is-failed" style={{ width: `${(counts.failed / total) * 100}%` }} />}
              {counts.attention > 0 && <i className="is-other" style={{ width: `${(counts.attention / total) * 100}%` }} />}
              {counts.scheduled > 0 && <i className="is-scheduled" style={{ width: `${(counts.scheduled / total) * 100}%` }} />}
            </span>
            <small className="health-source-summary__counts">
              <span>{counts.running} running</span><span>{counts.failed} failed</span><span>{counts.attention} attention</span><span>{counts.scheduled} scheduled</span>
            </small>
          </span>
        );
      })}
      {sources.length === 0 && <span className="preview-empty">No active jobs</span>}
    </span>
  );
}

function HealthDependencyPreview({ nodes, edges }: { nodes: HealthMapNode[]; edges: HealthMapEdge[] }) {
  const nodeById = new Map(nodes.map((node) => [node.id, node]));
  const priority = (edge: HealthMapEdge) => {
    const statusPriority = ["FAILED", "MISSING", "LATE", "LONG_RUNNING", "UNKNOWN"].includes(edge.status) ? 0 : 1;
    return statusPriority * 10 + (edge.relationshipType === "depends_on" ? 0 : 1);
  };
  const keyEdges = edges
    .filter((edge) => nodeById.has(edge.source) && nodeById.has(edge.target))
    .sort((left, right) => priority(left) - priority(right))
    .slice(0, 3);

  return (
    <span className="health-dependency-preview" aria-label="Key system dependencies">
      <small className="health-dependency-preview__eyebrow">DEPENDENCY FLOW</small>
      {keyEdges.map((edge) => (
        <span className="health-dependency-preview__edge" data-status={edge.status.toLowerCase()} key={edge.id}>
          <span title={nodeById.get(edge.source)?.label}>{nodeById.get(edge.source)?.label}</span>
          <span className="health-dependency-preview__connection"><i /><small title={edge.label}>{edge.label || "DEPENDS ON"}</small><i /></span>
          <span title={nodeById.get(edge.target)?.label}>{nodeById.get(edge.target)?.label}</span>
        </span>
      ))}
      {keyEdges.length === 0 && <span className="preview-empty">No dependency links available</span>}
    </span>
  );
}

function InvoiceVendors({ snapshot }: { snapshot: InvoiceAnalysisSnapshot }) {
  const counts = new Map<string, Set<string>>();
  snapshot.orders.forEach((order) => {
    const invoices = counts.get(order.vendor) || new Set<string>();
    invoices.add(order.invoiceId);
    counts.set(order.vendor, invoices);
  });
  const vendors = [...counts.entries()]
    .map(([vendor, invoices]) => ({ vendor, count: invoices.size }))
    .sort((left, right) => right.count - left.count)
    .slice(0, 3);
  const max = Math.max(1, ...vendors.map((vendor) => vendor.count));

  return (
    <span className="invoice-vendor-list" aria-label="Yesterday's invoices by vendor">
      {vendors.map(({ vendor, count }) => (
        <span className="invoice-vendor-list__row" key={vendor}>
          <span title={vendor}>{vendor}</span>
          <i><b style={{ width: `${(count / max) * 100}%` }} /></i>
          <small>{count}</small>
        </span>
      ))}
      {vendors.length === 0 && <span className="preview-empty">No invoices yesterday</span>}
    </span>
  );
}

function ModulePreview({
  moduleId,
  metrics,
  stationCount
}: {
  moduleId: string;
  metrics: LandingSnapshots;
  stationCount: number;
}) {
  if (moduleId === "stations") {
    const snapshot = metrics.stations;
    return (
      <span className="agent-bento-preview agent-bento-preview--stations">
        <span className="preview-main-metric">
          <small>YTD SALES</small>
          {snapshot ? <StatsCounter currency="CAD" value={snapshot.overview.totalSalesYtd.value} /> : snapshot === null ? <strong className="preview-pending">Unavailable</strong> : <KineticTextLoader className="kinetic-text-loader--compact" />}
        </span>
        <span className="preview-supporting-metrics">
          <span><small>STATIONS IN SCOPE</small><strong><StatsCounter value={snapshot?.stationPerformance.length ?? stationCount} /></strong></span>
          {snapshot && <span><small>GROSS PROFIT</small><strong><StatsCounter currency="CAD" value={snapshot.overview.grossProfitYtd.value} /></strong></span>}
        </span>
        {snapshot && <MiniBars label="Monthly station sales trend" values={snapshot.monthlyTrend.slice(-8).map((point) => point.cstoreSales + point.fuelSales)} />}
      </span>
    );
  }

  if (moduleId === "marketing") {
    const snapshot = metrics.marketing;
    return (
      <span className="agent-bento-preview agent-bento-preview--marketing">
        <span className="preview-main-metric">
          <small>WEB SESSIONS · {snapshot?.periodLabel || "CURRENT PERIOD"}</small>
          {snapshot ? <StatsCounter value={snapshot.web.sessions.value} /> : snapshot === null ? <strong className="preview-pending">Unavailable</strong> : <KineticTextLoader className="kinetic-text-loader--compact" />}
        </span>
        <span className="preview-supporting-metrics">
          {snapshot && <span><small>ACTIVE USERS</small><strong><StatsCounter value={snapshot.web.activeUsers.value} /></strong></span>}
          {snapshot && <span><small>SOCIAL ENGAGEMENTS</small><strong><StatsCounter value={snapshot.social.engagements.value} /></strong></span>}
        </span>
        {snapshot && <MiniBars label="Daily web sessions" values={snapshot.dailyTrend.slice(-8).map((point) => point.sessions)} />}
      </span>
    );
  }

  if (moduleId === "reports") {
    const snapshot = metrics.reports;
    const invoiceCount = snapshot ? new Set(snapshot.orders.map((order) => order.invoiceId).filter(Boolean)).size : 0;
    return (
      <span className="agent-bento-preview agent-bento-preview--reports">
        <span className="preview-main-metric">
          <small>YESTERDAY · INVOICES</small>
          {snapshot ? <StatsCounter value={invoiceCount} /> : snapshot === null ? <strong className="preview-pending">Unavailable</strong> : <KineticTextLoader className="kinetic-text-loader--compact" />}
        </span>
        {snapshot && <InvoiceVendors snapshot={snapshot} />}
      </span>
    );
  }

  if (moduleId === "profit-loss") {
    const snapshot = metrics.profitLoss;
    const revenue = snapshot?.overview.revenue.value || 0;
    const expenses = snapshot?.overview.operatingExpenses.value || 0;
    const profit = snapshot?.overview.netProfit.value || 0;
    const max = Math.max(1, Math.abs(revenue), Math.abs(expenses), Math.abs(profit));
    return (
      <span className="agent-bento-preview agent-bento-preview--profit-loss">
        <span className="preview-main-metric">
          <small>{snapshot?.periodLabel || "CURRENT PERIOD"} · REVENUE</small>
          {snapshot ? <StatsCounter currency={snapshot.currency} value={revenue} /> : snapshot === null ? <strong className="preview-pending">Unavailable</strong> : <KineticTextLoader className="kinetic-text-loader--compact" />}
        </span>
        {snapshot && <span className="profit-loss-bars" aria-label="Revenue, operating expenses, and net profit">
          <span><small>Revenue</small><i><b style={{ width: `${(Math.abs(revenue) / max) * 100}%` }} /></i><StatsCounter currency={snapshot.currency} value={revenue} /></span>
          <span><small>Operating expenses</small><i><b style={{ width: `${(Math.abs(expenses) / max) * 100}%` }} /></i><StatsCounter currency={snapshot.currency} value={expenses} /></span>
          <span><small>Net profit</small><i><b style={{ width: `${(Math.abs(profit) / max) * 100}%` }} /></i><StatsCounter currency={snapshot.currency} value={profit} /></span>
        </span>}
      </span>
    );
  }

  if (moduleId === "health") {
    const summary = metrics.health?.summary;
    const attentionJobs = summary ? summary.failedJobs + summary.missingJobs + summary.lateJobs + summary.longRunningJobs + summary.cancelledJobs + summary.unknownJobs : undefined;
    const loadingValue = metrics.health === undefined ? <KineticTextLoader className="kinetic-text-loader--compact" /> : <b>—</b>;
    return (
      <span className="agent-bento-preview agent-bento-preview--health">
        <span className="health-overview-line">
          <span><small>HEALTHY JOBS</small><strong>{summary ? <><StatsCounter value={summary.healthyJobs} /><i> / {summary.totalJobs}</i></> : metrics.health === null ? <b>Unavailable</b> : loadingValue}</strong></span>
          <span><small>RUNNING</small><strong className="is-running">{summary ? <StatsCounter value={summary.runningJobs} /> : loadingValue}</strong></span>
          <span><small>FAILED</small><strong className="is-failed">{summary ? <StatsCounter value={summary.failedJobs} /> : loadingValue}</strong></span>
          <span><small>NEEDS ATTENTION</small><strong className="is-attention">{attentionJobs === undefined ? loadingValue : <StatsCounter value={attentionJobs} />}</strong></span>
        </span>
        {metrics.health && <HealthSourceTypeSummary jobs={metrics.health.jobs} />}
        {metrics.healthMap ? <HealthDependencyPreview edges={metrics.healthMap.edges} nodes={metrics.healthMap.nodes} /> : metrics.healthMap === null ? <span className="preview-inline-note">Dependency map unavailable</span> : <KineticTextLoader className="kinetic-text-loader--compact" />}
        {metrics.health && <HealthRunChart runs={metrics.health.runs} />}
      </span>
    );
  }

  if (moduleId === "access-admin") {
    return (
      <span className="agent-bento-preview agent-bento-preview--simple">
        <span className="preview-main-metric"><small>STATIONS IN YOUR SCOPE</small><StatsCounter value={stationCount} /></span>
        <span className="preview-inline-note">Access and station assignments</span>
      </span>
    );
  }

  if (moduleId === "business-analysis") {
    const snapshot = metrics.businessAnalysis;
    return <span className="agent-bento-preview agent-bento-preview--simple"><span className="preview-main-metric"><small>ACTIVE OPPORTUNITIES</small>{snapshot ? <StatsCounter value={snapshot.aoiCount} /> : snapshot === null ? <strong className="preview-pending">Unavailable</strong> : <KineticTextLoader className="kinetic-text-loader--compact" />}</span>{snapshot && <span className="preview-inline-note">{snapshot.stationCount} stations · updated {snapshot.asOfDate}</span>}</span>;
  }

  return <span className="agent-bento-preview agent-bento-preview--simple"><span className="documentation-preview"><BookOpen size={20} aria-hidden="true" /><span>OPERATING GUIDES <i /> DATA DEFINITIONS <i /> RUNBOOKS</span></span></span>;
}

export function LandingPage({ onNavigate }: LandingPageProps) {
  const { hasAnyRole, portalContext, userLabel } = useAuthUser();
  const features = portalContext?.features;
  const canViewStations = Boolean(features?.stationAnalytics) || hasAnyRole(["StationAnalytics.User", "StationAnalytics.AccessAdmin", "HealthDashboard.Admin"]);
  const canViewReports = Boolean(features?.reports);
  const canViewProfitLoss = Boolean(features?.profitLoss);
  const canViewMarketing = Boolean(features?.marketingAnalytics) || hasAnyRole(["Marketing.Analytics"]);
  const canViewHealth = Boolean(features?.healthMonitoring) || hasAnyRole(["HealthDashboard.Reader", "HealthDashboard.Operator", "HealthDashboard.Admin"]);
  const canViewAccessAdmin = Boolean(features?.stationAccessAdmin) || hasAnyRole(["StationAnalytics.AccessAdmin", "HealthDashboard.Admin"]);
  const stationSks = useMemo(
    () => [...new Set((portalContext?.stations || []).map((station) => station.stationSk).filter((value): value is number => Number.isFinite(value)))],
    [portalContext?.stations]
  );
  const stationCount = portalContext?.stations.length || 0;
  const [metrics, setMetrics] = useState<LandingSnapshots>({});

  useEffect(() => {
    let isActive = true;
    const stationKey = stationSks.join(",");
    setMetrics({});

    const loadMetric = <K extends keyof LandingSnapshots>(key: K, request: Promise<NonNullable<LandingSnapshots[K]>>) => {
      void request.then((value) => {
        if (isActive) setMetrics((current) => ({ ...current, [key]: value }));
      }).catch(() => {
        if (isActive) setMetrics((current) => ({ ...current, [key]: null }));
      });
    };

    const timer = window.setTimeout(() => {
      if (canViewStations && stationSks.length > 0) loadMetric("stations", getStationAnalytics(stationSks));
      if (canViewReports) loadMetric("reports", getInvoiceAnalysis({ dateFrom: torontoDate(-1), dateTo: torontoDate(-1) }));
      if (canViewProfitLoss) loadMetric("profitLoss", getProfitLoss({ realDataOnly: true }));
      if (canViewMarketing) loadMetric("marketing", getMarketingAnalytics());
      if (canViewHealth) {
        const runsQuery = new URLSearchParams({ dateStart: torontoDate(-6), dateEnd: torontoDate() });
        loadMetric("health", Promise.all([
          getRequired<DashboardSummary>("/summary"),
          getRequired<IntegrationJob[]>("/jobs"),
          getRequired<IntegrationRun[]>(`/runs?${runsQuery.toString()}`)
        ]).then(([summary, jobs, runs]) => ({ summary, jobs: jobs.filter((job) => job.isActive), runs })));
        loadMetric("healthMap", getHealthMap({ realDataOnly: true }));
      }
      if (features?.businessAnalysis) loadMetric("businessAnalysis", getBusinessAnalysis({ realDataOnly: true }));
    }, 180);

    return () => {
      isActive = false;
      window.clearTimeout(timer);
    };
  }, [canViewHealth, canViewMarketing, canViewProfitLoss, canViewReports, canViewStations, features?.businessAnalysis, stationSks]);

  const availableModules = modules.filter((module) => {
    if (module.id === "stations") return canViewStations;
    if (module.id === "reports") return canViewReports;
    if (module.id === "profit-loss") return canViewProfitLoss;
    if (module.id === "marketing") return canViewMarketing;
    if (module.id === "health") return canViewHealth;
    if (module.id === "access-admin") return canViewAccessAdmin;
    return true;
  });

  return (
    <section className="page-stack landing-page">
      <header className="landing-heading">
        <div>
          <p>GEN7FUEL DATA CENTRE <span className="landing-live-indicator"><i /> LIVE WORKSPACE</span></p>
          <h2>Good to see you, {userLabel}</h2>
          <span className="landing-heading-subtitle">Choose a workspace to continue.</span>
        </div>
        <span className="landing-module-count">{String(availableModules.length).padStart(2, "0")} WORKSPACES</span>
      </header>

      <div className="agent-bento-grid" aria-label="Available workspaces">
        {availableModules.map((module, index) => {
          const Icon = module.icon;
          return (
            <button
              className={`agent-bento-card agent-bento-card--${module.tone}${module.id === "health" ? " agent-bento-card--health" : ""}`}
              data-index={index}
              key={module.id}
              onClick={() => onNavigate(module.view)}
              type="button"
            >
              <span className="agent-bento-card__topline">
                <span className="agent-bento-card__icon"><Icon size={19} aria-hidden={true} /></span>
                <span className="agent-bento-card__detail">{module.detail}</span>
                <ArrowUpRight className="agent-bento-card__arrow" size={17} aria-hidden="true" />
              </span>
              <span className="agent-bento-card__copy">
                <strong>{module.label}</strong>
                <span>{module.description}</span>
              </span>
              <ModulePreview moduleId={module.id} metrics={metrics} stationCount={stationCount} />
            </button>
          );
        })}
      </div>
    </section>
  );
}
